// submissionsApi — typed fetchers for /v1/admin/submissions (Phase 4 PR N).
// Backend: apps/tenant-api/src/admin-submissions.ts.

import { apiFetch } from "../../lib/api-client.js";

export interface SubmissionListItem {
  id: string;
  tenant_id: string;
  task_id: string;
  task_name: string | null;
  assignment_id: string;
  patrol_event_id: string | null;
  patrol_session_id: string | null;
  officer_id: string;
  officer_display_name: string | null;
  status: "in_progress" | "submitted" | "reviewed" | "expired" | "skipped";
  skip_reason: string | null;
  started_at: number;
  submitted_at: number | null;
  reviewed_at: number | null;
  location_id: string | null;
  location_label: string | null;
  site_id: string | null;
}

export interface SubmissionListResponse {
  items: SubmissionListItem[];
  has_more: boolean;
  correlation_id: string | null;
}

export interface SubmissionDetail {
  submission: {
    id: string; tenant_id: string; task_id: string; assignment_id: string;
    patrol_event_id: string | null; patrol_session_id: string | null;
    officer_id: string; status: string; skip_reason: string | null;
    verify_device_credential_id: string | null; verify_device_assertion_at: number | null;
    verify_face_photo_r2_key: string | null; verify_face_match_score: number | null;
    verify_face_pose_index: number | null; verify_face_liveness_action: string | null;
    verify_face_liveness_at: number | null;
    verify_geo_lat: number | null; verify_geo_lon: number | null;
    verify_geo_distance_m: number | null; verify_geo_at: number | null;
    text_response: string | null; photo_r2_keys: string | null; file_r2_keys: string | null;
    signature_r2_key: string | null;
    started_at: number; submitted_at: number | null; reviewed_at: number | null;
    user_agent: string | null; ip_hint: string | null;
  } | null;
  task: {
    id: string; name: string; description_short: string | null; instruction_doc: string | null;
    requires_text: number; requires_camera: number; requires_file: number;
    requires_signature: number; verify_device: number; verify_face: number;
    verify_geo: number; allow_skip: number;
  } | null;
  assignment: {
    id: string; target_kind: string; target_value: string;
    duration_minutes: number; grace_after_min: number;
    location_id: string; location_label: string | null; site_id: string | null;
    lat: number | null; lon: number | null; geofence_radius_m: number | null;
  } | null;
  officer: { id: string; display_name: string; status: string } | null;
  responses: Array<{
    id: string; responder_user_id: string; body: string;
    sent_to_line_at: number | null; line_message_id: string | null;
    created_at: number;
  }>;
  correlation_id: string | null;
}

export interface ListFilters {
  status?: string;
  site_id?: string;
  officer_id?: string;
  task_id?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
  months?: number;
}

export async function listSubmissions(filters: ListFilters = {}): Promise<SubmissionListResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) if (v != null && v !== "") qs.set(k, String(v));
  const r = await apiFetch(`/v1/admin/submissions?${qs.toString()}`);
  if (!r.ok) {
    const t = await r.text().catch(() => "");
    throw new Error(t || `HTTP ${r.status}`);
  }
  return (await r.json()) as SubmissionListResponse;
}

export async function getSubmission(id: string): Promise<SubmissionDetail> {
  const r = await apiFetch(`/v1/admin/submissions/${encodeURIComponent(id)}`);
  if (!r.ok) {
    const t = await r.text().catch(() => "");
    throw new Error(t || `HTTP ${r.status}`);
  }
  return (await r.json()) as SubmissionDetail;
}

export interface CreateResponseResult {
  ok: boolean;
  response_id: string;
  sent_to_line_at: number | null;
  line_message_id: string | null;
}

export async function createResponse(submissionId: string, body: string): Promise<CreateResponseResult> {
  const r = await apiFetch(
    `/v1/admin/submissions/${encodeURIComponent(submissionId)}/responses`,
    { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ body }) },
  );
  if (!r.ok) {
    const t = await r.text().catch(() => "");
    throw new Error(t || `HTTP ${r.status}`);
  }
  return (await r.json()) as CreateResponseResult;
}

const STATUS_LABELS: Record<string, string> = {
  in_progress: "進行中",
  submitted: "已提交",
  reviewed: "已審核",
  expired: "已過期",
  skipped: "已跳過",
};
export function statusLabel(s: string | null | undefined): string {
  return s ? (STATUS_LABELS[s] ?? s) : "—";
}

const STATUS_TONE: Record<string, "default" | "success" | "warning" | "danger" | "info"> = {
  in_progress: "info",
  submitted: "warning",
  reviewed: "success",
  expired: "danger",
  skipped: "danger",
};
export function statusTone(s: string | null | undefined): "default" | "success" | "warning" | "danger" | "info" {
  return s ? (STATUS_TONE[s] ?? "default") : "default";
}
